<template>
  <MapWrapper :sources="[...sources]" :layers="layers" v-bind="$attrs" @load="loadContours" />
</template>
<script setup>
const props = defineProps({
  sources: {
    type: Array,
    default: [],
  },
  layers: {
    type: Array,
    default: [],
  },
  showLabels: {
    type: Boolean,
    default: true,
  },
  // units for contour intervals and labels, "ft" or "m"
  units: {
    type: String,
    default: "ft",
  },
});

// contour intervals by zoom: [minor, major], in the selected units
const CONTOUR_THRESHOLDS = {
  ft: {
    9: [500, 1000],
    11: [200, 1000],
    12: [100, 500],
    14: [50, 200],
    15: [20, 100],
  },
  m: {
    9: [200, 1000],
    11: [100, 500],
    12: [50, 200],
    14: [20, 100],
    15: [10, 50],
  },
};

// Contour lines need several Mapterhorn elevation tiles for each map tile (the tile and its neighbors). They are
// added once the map is first idle, so the base map and the map's own data (e.g. snowfall) load first instead of
// sharing the connection with them, at a detail that suits the connection (contourOverzoom).
const demSource = shallowRef(null);
let overzoom = 1;

function loadContours(map) {
  map.once("idle", async () => {
    overzoom = contourOverzoom();
    if (overzoom === null) {
      return;
    }
    demSource.value = await loadDemSource();
  });
}

const sources = computed(() => [
  demSource.value && {
    id: "contour-source",
    type: "vector",
    tiles: [
      demSource.value.contourProtocolUrl({
        // elevation data is in meters
        multiplier: props.units === "m" ? 1 : 3.28084,
        thresholds: CONTOUR_THRESHOLDS[props.units] || CONTOUR_THRESHOLDS.ft,
        overzoom,
        // optional, override vector tile parameters:
        contourLayer: "contours",
        elevationKey: "ele",
        levelKey: "level",
        extent: 4096,
        buffer: 1,
      }),
    ],
    maxzoom: 15,
  },
  ...props.sources,
].filter(Boolean));
const contourLayers = computed(() => [
  {
    id: "contour-lines",
    type: "line",
    source: "contour-source",
    "source-layer": "contours",
    paint: {
      "line-color": "hsla(0, 0%, 55%, 0.35)",
      // level = highest index in thresholds array the elevation is a multiple of
      "line-width": ["match", ["get", "level"], 1, 1, 0.5],
    },
  },
  {
    id: "contour-labels",
    type: "symbol",
    source: "contour-source",
    "source-layer": "contours",
    filter: [">", ["get", "level"], 0],
    layout: {
      "symbol-placement": "line",
      "text-size": 10,
      "text-field": ["concat", ["number-format", ["get", "ele"], {}], props.units === "m" ? " m" : "'"],
      "text-font": ["Noto Sans Bold"],
      visibility: props.showLabels ? "visible" : "none",
    },
    paint: {
      "text-color": "#767676",
      "text-halo-blur": 0.5,
      "text-halo-color": "#ffffff",
      "text-halo-width": 1.5,
    },
  },
]);
const layers = computed(() => [...(demSource.value ? contourLayers.value : []), ...props.layers]);
</script>
